import { z } from 'zod';
import {
  DocumentMetadataSchema,
  IdSchema,
  LogicalDataTypeSchema,
  MetadataSchema,
  SchemaVersionSchema,
  SemanticTypeSchema,
  TimeGrainSchema,
} from './common.js';
import { AGGREGATE_FUNCTIONS, ExpressionSchema, FormulaTextSchema } from './expression.js';
import { FieldFormatSchema, FormatDefinitionSchema } from './format.js';

/**
 * Atributos documentales comunes: alimentan el diccionario de datos y la resolucion
 * de lenguaje natural (sinonimos). Todo objeto se referencia por `id`, nunca por nombre,
 * y los ids son unicos en TODO el modelo.
 */
const documented = {
  id: IdSchema,
  name: z.string().min(1).max(200),
  /** Etiqueta visible; si se omite se usa `name`. */
  label: z.string().min(1).max(200).optional(),
  description: z.string().max(4000).optional(),
  synonyms: z.array(z.string().min(1).max(200)).default([]),
  hidden: z.boolean().default(false),
  metadata: MetadataSchema.optional(),
};

// -----------------------------------------------------------------------------
// Tablas y columnas
// -----------------------------------------------------------------------------

export const SemanticColumnSchema = z.strictObject({
  ...documented,
  /** Nombre de la columna en la tabla del Dataset. */
  sourceColumn: z.string().min(1).max(256),
  dataType: LogicalDataTypeSchema,
  semanticType: SemanticTypeSchema.optional(),
  format: FieldFormatSchema.optional(),
  /** Columna usada para ordenar esta (por ejemplo, numero de mes para el nombre del mes). */
  sortByColumnId: IdSchema.optional(),
});
export type SemanticColumn = z.infer<typeof SemanticColumnSchema>;

export const SemanticTableSchema = z.strictObject({
  ...documented,
  datasetTableId: IdSchema,
  columns: z.array(SemanticColumnSchema).min(1),
});
export type SemanticTable = z.infer<typeof SemanticTableSchema>;

/** Columna calculada fila a fila sobre una tabla. No admite agregaciones ni medidas. */
export const CalculatedColumnSchema = z.strictObject({
  ...documented,
  tableId: IdSchema,
  dataType: LogicalDataTypeSchema,
  semanticType: SemanticTypeSchema.optional(),
  expression: ExpressionSchema,
  formula: FormulaTextSchema.optional(),
  format: FieldFormatSchema.optional(),
});
export type CalculatedColumn = z.infer<typeof CalculatedColumnSchema>;

// -----------------------------------------------------------------------------
// Dimensiones
// -----------------------------------------------------------------------------

export const DIMENSION_ROLES = ['categorical', 'temporal', 'geographic', 'identifier'] as const;
export const DimensionRoleSchema = z.enum(DIMENSION_ROLES);
export type DimensionRole = z.infer<typeof DimensionRoleSchema>;

/** Dimension de negocio basada en una columna (fisica o calculada). */
export const DimensionSchema = z.strictObject({
  ...documented,
  columnId: IdSchema,
  role: DimensionRoleSchema,
  hierarchyId: IdSchema.optional(),
  format: FieldFormatSchema.optional(),
  defaultTimeGrain: TimeGrainSchema.optional(),
});
export type Dimension = z.infer<typeof DimensionSchema>;

/** Dimension definida por una expresion fila a fila (por ejemplo, rangos de importe). */
export const CalculatedDimensionSchema = z.strictObject({
  ...documented,
  /** Tabla que aporta el grano de la expresion. */
  tableId: IdSchema,
  dataType: LogicalDataTypeSchema,
  role: DimensionRoleSchema,
  expression: ExpressionSchema,
  formula: FormulaTextSchema.optional(),
  hierarchyId: IdSchema.optional(),
  format: FieldFormatSchema.optional(),
});
export type CalculatedDimension = z.infer<typeof CalculatedDimensionSchema>;

// -----------------------------------------------------------------------------
// Medidas
// -----------------------------------------------------------------------------

export const AggregationSchema = z.enum(AGGREGATE_FUNCTIONS);
export type Aggregation = z.infer<typeof AggregationSchema>;

/**
 * Medida base: agregacion de una columna. `count` sin columna cuenta filas de `tableId`.
 * No contiene SQL; el dialecto decide como agregar.
 */
export const MeasureSchema = z.strictObject({
  ...documented,
  aggregation: AggregationSchema,
  columnId: IdSchema.optional(),
  /** Tabla "hogar"; obligatoria cuando no hay `columnId` (conteo de filas). */
  tableId: IdSchema.optional(),
  /** Filtro fila a fila aplicado antes de agregar. */
  filter: ExpressionSchema.optional(),
  format: FieldFormatSchema.optional(),
});
export type Measure = z.infer<typeof MeasureSchema>;

/** Medida calculada: expresion sobre agregaciones y otras medidas (DIVIDE, IF...). */
export const CalculatedMeasureSchema = z.strictObject({
  ...documented,
  expression: ExpressionSchema,
  formula: FormulaTextSchema.optional(),
  tableId: IdSchema.optional(),
  format: FieldFormatSchema.optional(),
});
export type CalculatedMeasure = z.infer<typeof CalculatedMeasureSchema>;

// -----------------------------------------------------------------------------
// Relaciones, jerarquias y KPIs
// -----------------------------------------------------------------------------

export const RelationshipSchema = z.strictObject({
  id: IdSchema,
  fromColumnId: IdSchema,
  toColumnId: IdSchema,
  cardinality: z.enum(['one-to-one', 'one-to-many', 'many-to-one', 'many-to-many']),
  crossFilter: z.enum(['single', 'both']).default('single'),
  active: z.boolean().default(true),
});
export type Relationship = z.infer<typeof RelationshipSchema>;

export const HierarchyLevelSchema = z.strictObject({
  id: IdSchema,
  name: z.string().min(1).max(200),
  dimensionId: IdSchema,
});
export type HierarchyLevel = z.infer<typeof HierarchyLevelSchema>;

export const HierarchySchema = z.strictObject({
  ...documented,
  levels: z.array(HierarchyLevelSchema).min(1),
});
export type Hierarchy = z.infer<typeof HierarchySchema>;

export const KpiSchema = z.strictObject({
  ...documented,
  measureId: IdSchema,
  target: z
    .discriminatedUnion('kind', [
      z.strictObject({ kind: z.literal('value'), value: z.number() }),
      z.strictObject({ kind: z.literal('measure'), measureId: IdSchema }),
    ])
    .optional(),
  direction: z.enum(['higher-is-better', 'lower-is-better']),
  /** Umbrales expresados como proporcion del objetivo (1 = 100 %). */
  thresholds: z
    .strictObject({ warning: z.number().positive(), critical: z.number().positive() })
    .optional(),
  format: FieldFormatSchema.optional(),
});
export type Kpi = z.infer<typeof KpiSchema>;

// -----------------------------------------------------------------------------
// Reglas, diccionario, seguridad y ajustes
// -----------------------------------------------------------------------------

export const BusinessRuleSchema = z.strictObject({
  id: IdSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(4000).optional(),
  appliesTo: z.strictObject({
    kind: z.enum(['model', 'table', 'column', 'dimension', 'measure']),
    targetId: IdSchema.optional(),
  }),
  rule: z.discriminatedUnion('type', [
    /** Filtro de filas aplicado siempre (por ejemplo, excluir registros anulados). */
    z.strictObject({ type: z.literal('row-filter'), expression: ExpressionSchema }),
    /** Validacion de calidad con mensaje para el usuario. */
    z.strictObject({
      type: z.literal('validation'),
      expression: ExpressionSchema,
      message: z.string().min(1).max(2000),
      severity: z.enum(['info', 'warning', 'error']).default('warning'),
    }),
  ]),
  enabled: z.boolean().default(true),
});
export type BusinessRule = z.infer<typeof BusinessRuleSchema>;

export const DataDictionaryEntrySchema = z.strictObject({
  id: IdSchema,
  term: z.string().min(1).max(200),
  definition: z.string().min(1).max(4000),
  relatedObjectIds: z.array(IdSchema).default([]),
  owner: z.string().min(1).max(200).optional(),
  tags: z.array(z.string().min(1).max(64)).default([]),
});
export type DataDictionaryEntry = z.infer<typeof DataDictionaryEntrySchema>;

/**
 * Seguridad declarativa del modelo: roles con filtros de filas y visibilidad de
 * objetos. La asignacion de usuarios a roles y las credenciales viven fuera del modelo.
 */
export const SecurityRoleSchema = z.strictObject({
  id: IdSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  rowFilters: z
    .array(z.strictObject({ id: IdSchema, tableId: IdSchema, expression: ExpressionSchema }))
    .default([]),
  objectPermissions: z
    .array(z.strictObject({ objectId: IdSchema, access: z.enum(['hidden', 'read']) }))
    .default([]),
});
export type SecurityRole = z.infer<typeof SecurityRoleSchema>;

export const ModelSecuritySchema = z.strictObject({
  defaultAccess: z.enum(['allow', 'deny']).default('allow'),
  roles: z.array(SecurityRoleSchema).default([]),
});
export type ModelSecurity = z.infer<typeof ModelSecuritySchema>;

export const ModelSettingsSchema = z.strictObject({
  /** Etiqueta BCP 47, por ejemplo `es-MX`. */
  locale: z.string().min(2).max(35).default('es'),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/, 'Codigo ISO 4217')
    .optional(),
  /** Zona horaria IANA, por ejemplo `America/Mexico_City`. */
  timezone: z.string().min(1).max(64).optional(),
  fiscalYearStartMonth: z.number().int().min(1).max(12).default(1),
  primaryDateDimensionId: IdSchema.optional(),
  defaultFormatId: IdSchema.optional(),
});
export type ModelSettings = z.infer<typeof ModelSettingsSchema>;

/** Linaje: fuentes que alimentan el modelo, sin datos de conexion. */
export const ModelSourceSchema = z.strictObject({
  dataSourceId: IdSchema,
  datasetTableIds: z.array(IdSchema).default([]),
});
export type ModelSource = z.infer<typeof ModelSourceSchema>;

/**
 * Modelo semantico: COMO se interpretan los datos para analisis. Capa de negocio
 * sobre un Dataset, independiente de la fuente fisica. No contiene estado de analisis,
 * resultados, SQL, credenciales ni configuracion de UI.
 */
export const SemanticModelSchema = z.strictObject({
  schemaVersion: SchemaVersionSchema,
  id: IdSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(4000).optional(),
  metadata: DocumentMetadataSchema.optional(),
  datasetId: IdSchema,
  sources: z.array(ModelSourceSchema).default([]),
  tables: z.array(SemanticTableSchema).min(1),
  calculatedColumns: z.array(CalculatedColumnSchema).default([]),
  dimensions: z.array(DimensionSchema).default([]),
  calculatedDimensions: z.array(CalculatedDimensionSchema).default([]),
  measures: z.array(MeasureSchema).default([]),
  calculatedMeasures: z.array(CalculatedMeasureSchema).default([]),
  relationships: z.array(RelationshipSchema).default([]),
  hierarchies: z.array(HierarchySchema).default([]),
  kpis: z.array(KpiSchema).default([]),
  formats: z.array(FormatDefinitionSchema).default([]),
  businessRules: z.array(BusinessRuleSchema).default([]),
  dataDictionary: z.array(DataDictionaryEntrySchema).default([]),
  security: ModelSecuritySchema.prefault({}),
  settings: ModelSettingsSchema.prefault({}),
});
export type SemanticModel = z.infer<typeof SemanticModelSchema>;
/** Forma de entrada (antes de aplicar valores por defecto). */
export type SemanticModelInput = z.input<typeof SemanticModelSchema>;
